import React, { useEffect, useRef, useState } from 'react';
import './HomeDashboardCards.css';

/**
 * Shared streak card (home dashboard + celebration overlay).
 * When animateFrom is set and less than count, the day number counts up.
 */
export default function StreakDashboardCard({
  count = 1,
  animateFrom = null,
  innerRef = null,
  className = '',
  fast = false,
  buttonInteractive = true,
  onRemindClick,
  gradientId = 'home-dash-ig-grad',
}) {
  const target = Math.max(1, count || 1);
  const fromValue = animateFrom != null ? Math.max(1, animateFrom) : target;
  const [displayCount, setDisplayCount] = useState(fromValue);
  const [countTick, setCountTick] = useState(false);
  const tickTimeoutRef = useRef(null);

  const pulseCount = () => {
    setCountTick(true);
    if (tickTimeoutRef.current) clearTimeout(tickTimeoutRef.current);
    tickTimeoutRef.current = window.setTimeout(() => setCountTick(false), 350);
  };

  useEffect(() => () => {
    if (tickTimeoutRef.current) clearTimeout(tickTimeoutRef.current);
  }, []);

  useEffect(() => {
    if (fromValue === target) {
      setDisplayCount(target);
      pulseCount();
      return undefined;
    }
    if (target < fromValue) {
      setDisplayCount(target);
      pulseCount();
      return undefined;
    }

    const stepMs = fast ? 70 : 180;
    let current = fromValue;
    setDisplayCount(fromValue);
    const intervalId = window.setInterval(() => {
      current += 1;
      setDisplayCount(current);
      pulseCount();
      if (current >= target) {
        window.clearInterval(intervalId);
      }
    }, stepMs);
    return () => window.clearInterval(intervalId);
  }, [fromValue, target, fast]);

  const setRef = (el) => {
    if (innerRef && typeof innerRef === 'object' && 'current' in innerRef) {
      innerRef.current = el;
    }
  };

  return (
    <article
      className={`home-dash-card home-dash-card-streak ${className}`.trim()}
      ref={setRef}
      data-streak-count={displayCount}
    >
      <div
        className="home-dash-card-body"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/streak_bg.png)`,
        }}
      >
        <p
          className={`home-dash-headline-lg${countTick ? ' streak-count-tick' : ''}`}
          aria-hidden="true"
        >
          {displayCount}
        </p>
        <p className="home-dash-headline-md">Day streak</p>
        <p className="home-dash-card-sub">Come back tomorrow to keep it</p>
        <button
          type="button"
          className="home-dash-btn home-dash-btn-outline"
          tabIndex={buttonInteractive ? 0 : -1}
          aria-hidden={buttonInteractive ? undefined : true}
          disabled={!buttonInteractive}
          onClick={buttonInteractive ? onRemindClick : undefined}
        >
          <svg className="home-dash-btn-outline-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"
              stroke={`url(#${gradientId})`}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13.73 21a2 2 0 0 1-3.46 0"
              stroke={`url(#${gradientId})`}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="home-dash-btn-outline-text">Creators Streak</span>
        </button>
      </div>
    </article>
  );
}
